import { BaseModuleI, ModuleI, OptionI } from "../../interfaces";
import { getAllModulesByProfileId } from "../../apis";
import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { useModulesBackupStore } from "./router-backup.store";
import { DashboardType, DashboardTypeEnum } from "../../enums";

type State = {
  options: OptionI[];
  modules: BaseModuleI[];
  moduleMap: Record<string, BaseModuleI>;
  clientId: number | null;
  typeUserCode: string;
  role: string | null;
  client: string | null;
  profileId: string;
  avatar: string;
  cloneCatalogue: string | null;
  cloneTask: number | null;
  section: string;
  typeDashboardCode: DashboardType;
  firstName: string;
  isLoading: boolean;
  previousModuleBase: string;
  fullName: string;
  eventId:string|null
  eventName:string|null
  // OPTIMIZACIÓN: Cache para evitar llamadas innecesarias
  lastModulesUpdate: number;
  isValidatingModules: boolean;
  currentDateKey: string;
  byPassSw: boolean;
  // SUBSCRIPTION
  subscriptionStatusCode: string | null;
  subscriptionStatus: string | null;
  daysLeft: number | null;
};

type Actions = {
  setEventId: (eventId: State["eventId"]) => void;
  setEventName: (eventName: State["eventName"]) => void;
  setByPassSw: (byPassSw: State["byPassSw"]) => void;
  setClientId: (clientId: State["clientId"]) => void;
  setRole: (role: State["role"]) => void;
  setClient: (client: State["client"]) => void;
  setTypeUserCode: (typeUserCode: State["typeUserCode"]) => void;
  setPreviousModuleBase: (
    previousModuleBase: State["previousModuleBase"],
  ) => void;
  setLoading: (loading: State["isLoading"]) => void;
  setProfileId: (profileId: string) => void;
  setFirstName: (firstName: string) => void;
  setTypeDashboardCode: (typeDashboardCode: DashboardType) => void;
  setOptions: (options: OptionI[]) => void;
  setModules: (modules: ModuleI[]) => void;
  setAvatar: (avatar: string) => void;
  setFullName: (fullName: string) => void;
  reset: () => void;
  resetOptions: () => void;
  validateModulesFromApi: () => Promise<any>;
  resetModules: () => void;
  getModulesFromApi: () => void;
  setCloneCatalogue: (catalogue: State["cloneCatalogue"]) => void;
  setCloneTask: (task: State["cloneTask"]) => void;
  setSection: (section: State["section"]) => void;
  // OPTIMIZACIÓN: Nuevas acciones para mejor control
  forceRefreshModules: () => Promise<any>;
  getModulesFromCache: () => ModuleI[];
  setSubscriptionStatus: (
    code: string | null,
    status: string | null,
    days: number | null,
  ) => void;
};

const areModulesEqual = (a: ModuleI[], b: ModuleI[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (mod, index) => mod.id === b[index]?.id && mod.to === b[index]?.to,
  );
};

const buildModuleMap = (
  modules: BaseModuleI[],
  map: Record<string, BaseModuleI> = {},
): Record<string, BaseModuleI> => {
  for (const module of modules) {
    if (module.to) {
      map[module.to] = module;
    }
    if (module.children && module.children.length > 0) {
      buildModuleMap(module.children, map);
    }
  }
  return map;
};

const initialState: State = {
  eventId:null,
  eventName:null,
  byPassSw: false,
  clientId: null,
  role: null,
  moduleMap: {},
  client: null,
  options: [],
  modules: [],
  profileId: "",
  firstName: "",
  avatar: "",
  typeUserCode: "",
  typeDashboardCode: DashboardTypeEnum.GENERIC,
  cloneCatalogue: null,
  cloneTask: null,
  section: "",
  isLoading: false,
  previousModuleBase: "",
  fullName: "",

  // OPTIMIZACIÓN: Nuevos campos
  lastModulesUpdate: 0,
  isValidatingModules: false,
  currentDateKey: new Date().toDateString(),

  // SUBSCRIPTION
  subscriptionStatusCode: null,
  subscriptionStatus: null,
  daysLeft: null,
};

export const useRouterStore = create<State & Actions>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        eventId:null,
        eventName:null,
        currentDateKey: new Date().toDateString(),
        byPassSw: false,
        clientId: null,
        role: null,
        client: null,
        typeUserCode: "",
        options: [],
        modules: [],
        moduleMap: {},
        profileId: "",
        firstName: "",
        avatar: "",
        cloneCatalogue: null,
        cloneTask: null,
        section: "",
        typeDashboardCode: DashboardTypeEnum.GENERIC,
        previousModuleBase: "",
        isLoading: false,
        fullName: "",
        lastModulesUpdate: 0,
        isValidatingModules: false,
        // SUBSCRIPTION
        subscriptionStatusCode: null,
        subscriptionStatus: null,
        daysLeft: null,

        setEventId: (eventId) => set(() => ({ eventId })),
        setEventName: (eventName) => set(() => ({ eventName })),
        setByPassSw: (byPassSw) => set(() => ({ byPassSw })),
        setClient: (client) => set(() => ({ client })),
        setClientId: (clientId) => set(() => ({ clientId })),
        setRole: (role) => set(() => ({ role })),
        setTypeUserCode: (typeUserCode) => set(() => ({ typeUserCode })),
        setFullName: (fullName) => set((state) => ({ fullName })),
        setPreviousModuleBase: (previousModuleBase) =>
          set((state) => ({ previousModuleBase })),
        setLoading: (loading) => set({ isLoading: loading }),
        reset: () => set(initialState),
        setFirstName: (firstName) => set(() => ({ firstName })),
        setSection: (section) => set(() => ({ section })),
        setSubscriptionStatus: (code, status, days) =>
          set(() => ({
            subscriptionStatusCode: code,
            subscriptionStatus: status,
            daysLeft: days,
          })),

        // OPTIMIZACIÓN: Resetear opciones sin comparación
        resetOptions: () => {
          set({ options: [] });
        },

        resetModules: () =>
          set((state) => ({ modules: [], lastModulesUpdate: 0 })),

        getModulesFromApi: async () => {
          // Implementación simplificada - mantener si es necesaria
        },

        // OPTIMIZACIÓN: Función para obtener módulos desde cache
        getModulesFromCache: () => {
          return get().modules;
        },

        // OPTIMIZACIÓN: Función para forzar refresh de módulos
        forceRefreshModules: async () => {
          const state = get();
          set({ isValidatingModules: true, isLoading: true });

          try {
            const tokenData = JSON.parse(
              localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}",
            );
            const token = tokenData?.token ?? null;
            const profileId = tokenData?.user?.profileId ?? state.profileId;

            if (!token || !profileId) return;

            const { data: dataModules } =
              await getAllModulesByProfileId(profileId);
            if (dataModules) {
              // Solo actualizar si los módulos son diferentes
              if (!areModulesEqual(state.modules, dataModules)) {
                const opts = Array.from(
                  new Map(
                    dataModules
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option]),
                  ).values(),
                );
                const moduleMap = buildModuleMap(dataModules);

                set({
                  modules: dataModules,
                  options: opts,
                  moduleMap,
                  lastModulesUpdate: Date.now(),
                });

                useModulesBackupStore.getState().setModulesBackup(dataModules);
              }
            }

            return dataModules;
          } catch (error: any) {
            const { modulesBackup } = useModulesBackupStore.getState();

            if (!navigator.onLine || error.response?.status === 401) {
              if (
                modulesBackup.length > 0 &&
                !areModulesEqual(state.modules, modulesBackup)
              ) {
                const opts = Array.from(
                  new Map(
                    modulesBackup
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option]),
                  ).values(),
                );

                set({
                  modules: modulesBackup,
                  options: opts,
                });
                return modulesBackup;
              }
            }

            return;
          } finally {
            set({ isLoading: false, isValidatingModules: false });
          }
        },

        // OPTIMIZACIÓN: Validación inteligente de módulos
        validateModulesFromApi: async () => {
          const state = get();

          // Evitar llamadas duplicadas
          if (state.isValidatingModules) {
            return state.modules;
          }

          // Si ya tenemos módulos y fueron actualizados recientemente (menos de 5 minutos), no validar
          const fiveMinutes = 5 * 60 * 1000;
          if (
            state.modules.length > 0 &&
            Date.now() - state.lastModulesUpdate < fiveMinutes
          ) {
            return state.modules;
          }

          set({ isValidatingModules: true, isLoading: true });

          try {
            const tokenData = JSON.parse(
              localStorage.getItem(process.env.REACT_APP_TOKEN!) || "{}",
            );
            const token = tokenData?.token ?? null;
            const profileId = tokenData?.user?.profileId ?? state.profileId;

            if (!token || !profileId) {
              set({ isLoading: false, isValidatingModules: false });
              return state.modules;
            }

            const { data: dataModules } =
              await getAllModulesByProfileId(profileId);

            if (dataModules) {
              // Solo actualizar si los datos son diferentes
              if (!areModulesEqual(state.modules, dataModules)) {
                const opts = Array.from(
                  new Map(
                    dataModules
                      .flatMap((mod) => mod.options || [])
                      .map((option) => [option.id, option]),
                  ).values(),
                );
                const moduleMap = buildModuleMap(dataModules);

                set({
                  modules: dataModules,
                  options: opts,
                  moduleMap,
                  lastModulesUpdate: Date.now(),
                });

                useModulesBackupStore.getState().setModulesBackup(dataModules);
                return dataModules;
              } else {
                // Los datos son iguales, solo actualizar timestamp
                set({ lastModulesUpdate: Date.now() });
                return state.modules;
              }
            } else {
              if (state.modules.length > 0) {
                // No limpiar módulos existentes si la respuesta está vacía
                return state.modules;
              }
              set({ modules: [], options: [] });
              return [];
            }
          } catch (error: any) {
            const { modulesBackup } = useModulesBackupStore.getState();

            if (!navigator.onLine || error.response?.status === 401) {
              if (modulesBackup.length > 0) {
                if (!areModulesEqual(state.modules, modulesBackup)) {
                  const opts = Array.from(
                    new Map(
                      modulesBackup
                        .flatMap((mod) => mod.options || [])
                        .map((option) => [option.id, option]),
                    ).values(),
                  );

                  set({
                    modules: modulesBackup,
                    options: opts,
                  });
                }
                return modulesBackup;
              }
            }

            // No limpiar módulos en caso de error si ya tenemos algunos
            if (state.modules.length === 0) {
              set({ modules: [], options: [] });
            }
            return state.modules;
          } finally {
            set({ isLoading: false, isValidatingModules: false });
          }
        },

        // OPTIMIZACIÓN: Actualizar opciones sin comparación estricta para debugging
        setOptions: (options) => {
          set({ options });
        },

        // OPTIMIZACIÓN: Solo actualizar módulos si son diferentes
        setModules: (modules) =>
          set((state) => {
            if (areModulesEqual(state.modules, modules)) {
              return state;
            }
            const moduleMap = buildModuleMap(modules);
            return {
              modules,
              moduleMap,
              lastModulesUpdate: Date.now(),
            };
          }),

        setTypeDashboardCode: (typeDashboardCode) =>
          set((state) => ({ typeDashboardCode })),
        setProfileId: (profileId) => set((state) => ({ profileId })),
        setAvatar: (avatar) => set((state) => ({ avatar })),
        setCloneCatalogue: (cloneCatalogue) =>
          set((state) => ({ cloneCatalogue })),
        setCloneTask: (cloneTask) => set((state) => ({ cloneTask })),
      }),
      {
        name: process.env.REACT_APP_TOKEN_ROUTER!,
        // OPTIMIZACIÓN: Solo persistir campos necesarios
        partialize: (state) => ({
          clientId: state.clientId,
          client: state.client,
          profileId: state.profileId,
          firstName: state.firstName,
          avatar: state.avatar,
          typeDashboardCode: state.typeDashboardCode,
          fullName: state.fullName,
          typeUserCode: state.typeUserCode,
          currentDateKey: state.currentDateKey,
          subscriptionStatusCode: state.subscriptionStatusCode,
          subscriptionStatus: state.subscriptionStatus,
          daysLeft: state.daysLeft,
        }),
        onRehydrateStorage: () => (state) => {
          console.log("REHIDRATADO");
        },
      },
    ),
  ),
);
